/**
 * <strong>Purpose:</strong> <br>
 * Provides ability to manage focusing of FocusableMenuChildMixin Widgets in a Menu
 * <br><br>
 * <strong>Contract:</strong> <br>
 * Do mix in ContainerFocusMixin.
 *
 * @module mw-form/mixins/MenuFocusMixin
 *
 * @copyright Copyright 2015-2020 The MathWorks, Inc.
 */

define([
    'dojo/_base/declare',
    'dojo/_base/lang',
    'dojo/on',
    'dojo/has',
    'dojo/touch',
    'dojo/keys',
    'dijit/popup',
    'dijit/registry',
    'mw-dom-utils/domUtils',
    'mw-utils/widgetUtils',
    'mw-form/mixins/ContainerFocusMixin'
], function (declare, lang, on, has, touch, keys, popup, registry, domUtils, widgetUtils, ContainerFocusMixin) {
    var touchOnly = has('touch') && (!window.PointerEvent);

    var MenuFocusMixin = declare(null, {
        postCreate: function () {
            this.inherited(arguments);
            this.domNode.classList.add('mwMenuFocusMixin');
            this.focusNode = this.focusNode || this.domNode;
            this.own(on(this.domNode, 'open', this._handleOpen.bind(this)));
            this.own(on(this.domNode, 'close', this._handleClose.bind(this)));
        },

        _handleOpen: function () {
            this._addFocusListeners();
        },

        _handleClose: function () {
            this._removeFocusListeners();
        },

        _addFocusListeners: function () {
            var mouseEventType;
            if (this.focusNode) {
                this._focusNodeBlurListener = on(this.focusNode, 'blur', this._handleBlur.bind(this));
                this.own(this._focusNodeBlurListener);
                this._windowBlurListener = on(window, 'blur', this._handleBlur.bind(this));
                this.own(this._windowBlurListener);

                // g2107710: If pointer events are available use them, else use mouse events. This helps in a use
                // case where clicking on any element that does preventDefault on pointerDown won't prevent the
                // '_handleMouseEvent' callback from being executed.
                if (window.PointerEvent) {
                    mouseEventType = 'pointerdown';
                } else {
                    mouseEventType = 'mousedown';
                }
                this._documentClickListener = on(document.body, touchOnly ? touch.press : mouseEventType, this._handleMouseEvent.bind(this));
                this.own(this._documentClickListener);
                this._escapeKeydownListener = on(document.body, 'keydown', this._handleKeydown.bind(this));
                this.own(this._escapeKeydownListener);
            }
        },

        _removeFocusListeners: function () {
            if (this._focusNodeBlurListener) {
                this._focusNodeBlurListener.remove();
                delete this._focusNodeBlurListener;
            }
            if (this._windowBlurListener) {
                this._windowBlurListener.remove();
                delete this._windowBlurListener;
            }
            if (this._documentClickListener) {
                this._documentClickListener.remove();
                delete this._documentClickListener;
            }
            if (this._escapeKeydownListener) {
                this._escapeKeydownListener.remove();
                delete this._escapeKeydownListener;
            }
        },

        _handleBlur: function (e) {
            setTimeout(() => {
                this._handlePossibleFocusChange(e);
            }, 0);
        },

        _handleMouseEvent: function (e) {
            this._handlePossibleFocusChange(e);
        },

        _handleKeydown: function (e) {
            if (e.keyCode === keys.ESCAPE) {
                this._handlePossibleFocusChange(e);
            }
        },

        _handlePossibleFocusChange: function (e) {
            if (widgetUtils.isMenuOpen(this)) {
                var gainedFocusWidget = this._getGainedFocusWidgetFromEvent(e);
                var gainedFocusWidgetIsDescendant;
                var gainedFocusWidgetIsChildContextMenu;
                var gainedFocusWidgetIsInvokingWidget;

                gainedFocusWidgetIsDescendant = this.isDescendant(gainedFocusWidget, this);
                gainedFocusWidgetIsChildContextMenu = this._isChildContextMenu(gainedFocusWidget);
                gainedFocusWidgetIsInvokingWidget = (this._invokingWidget && (gainedFocusWidget === this._invokingWidget));

                if ((!gainedFocusWidget) ||
                    ((!gainedFocusWidgetIsDescendant) &&
                     (!gainedFocusWidgetIsChildContextMenu) &&
                     (!gainedFocusWidgetIsInvokingWidget))) { // focus change is possible
                    this._focusChange(gainedFocusWidget);
                }
            }
        },

        _getGainedFocusWidgetFromEvent: function (e) {
            var gainedFocusWidget = null;
            if (e instanceof FocusEvent) { // eslint-disable-line no-undef
                gainedFocusWidget = registry.getEnclosingWidget(domUtils.parseFocusEvent(e).gainedFocusNode);
            } else { // can be MouseEvent, PointerEvent, KeyboardEvent, etc.
                gainedFocusWidget = registry.getEnclosingWidget(e.target) ||
                (document.activeElement.contains(e.target) // If the activeElement does not contain the target, then the target rejected focus
                    ? registry.getEnclosingWidget(document.activeElement)
                    : null);
            }

            // update gainedFocusWidget in case it is a composite widget
            var node = null;
            if (gainedFocusWidget) {
                node = (gainedFocusWidget.focusNode && (gainedFocusWidget.focusNode instanceof Element)) // eslint-disable-line no-undef
                    ? gainedFocusWidget.focusNode : gainedFocusWidget.domNode;
            }
            // TODO: Update logic when CompositionMixin is added
            // If the input is a child in a composite widget, then get the primary node from the value of the 'data-composite-child' attribute
            if (node && node.hasAttribute('data-composite-child')) {
                gainedFocusWidget = registry.getEnclosingWidget(document.getElementById(node.getAttribute('data-composite-primary-id')));
            }

            return gainedFocusWidget;
        },

        _isChildContextMenu: function (widget) { // returns true if widget is a context menu for an item in the menu
            return widget &&
                widget.domNode &&
                ([].slice.call(document.querySelectorAll('.mwToolstripPopupContextMenu, .mwToolstripPopupContextMenu *')).indexOf(widget.domNode) > -1);
        },

        // WIDGET BASED METHODS
        _focusChange: function (gainedFocusWidget) {
            if (this._closeEventData) {
                if (this._closeEventData.mwEventData) {
                    this._closeEventData.mwEventData.gainedFocusWidget = gainedFocusWidget;
                } else {
                    this._closeEventData.mwEventData = {
                        gainedFocusWidget: gainedFocusWidget
                    };
                }
            } else {
                this._closeEventData = {
                    mwEventData: {
                        gainedFocusWidget: gainedFocusWidget
                    }
                };
            }

            if (gainedFocusWidget == null) {
                // All children of Menu-like widgets are widgets, and thus if gainedFocusWidget is not a widget, we can safely close
                this.closeRequest(false);
                return;
            }
            if (!this.isInvokingWidget(gainedFocusWidget) && !this.isDescendant(gainedFocusWidget, this)) {
                var menu = gainedFocusWidget;

                // If you right click on the MenuItem and as soon as the context menu opens, you move the mouse
                // inside the context menu to change the focus, in that case gainedFocusWidget will be the MenuItem
                // instead of Menu itself. The following 3 lines are important because _lastKnownInvokingNode
                // property is on the Menu and not on the MenuItem.
                if (gainedFocusWidget && gainedFocusWidget.domNode && gainedFocusWidget.getParent && gainedFocusWidget.getParent()) {
                    menu = gainedFocusWidget.getParent();
                }

                // To make sure right clicking on the MenuItem does not close the Menu itself
                if (!menu.invokedFromListItemTypeWidget) {
                    this.closeRequest(false);
                }
            }
        },

        closeRequest: function (focusParent) {
            if (focusParent == null || [true, false].indexOf(focusParent) < 0) {
                focusParent = true;
            }
            // ContextMenu
            if (this.close) {
                // Close was requested by child item, so allow child item's action to manage focus
                // TODO: Investigate if we want a hook to allow users to determine this behavior
                // delete this._closeEventData.mwFocusData;

                this.close();
            }
            // MenuMixin
            if (this._invokingWidget && this._invokingWidget.isMenuOpen()) {
                this._invokingWidget.closeMenu(focusParent);
            }
            // If the popup is not closed at this point then resort to directly using popup.js
            // The display style of the popup wrapper will be '' if open and 'none' if closed
            if (widgetUtils.isMenuOpen(this)) {
                popup.close(this);
            }
        },

        isInvokingWidget: function (widget) {
            if (widget == null) {
                return false;
            }

            if (widget instanceof HTMLElement) { // eslint-disable-line no-undef
                widget = registry.getEnclosingWidget(widget);
            }

            return (widget === this._invokingWidget);
        },

        isDescendant: function (child, ancestor) {
            if (child == null) {
                return false;
            } else if (child.domNode) {
                child = child.domNode;
            }

            if (ancestor == null) {
                ancestor = this.domNode;
            } else if (ancestor.domNode) {
                ancestor = ancestor.domNode;
            }

            var getSpouseNode = function getSpouseNode (childNode) {
                if (childNode.hasAttribute('dijitpopupparent')) {
                    return document.getElementById(childNode.getAttribute('dijitpopupparent'));
                }
                // return null by default
                return null;
            };

            if (!(child instanceof HTMLElement) || !(ancestor instanceof HTMLElement)) { // eslint-disable-line no-undef
                return false;
            } else {
                // If node is a direct descendant or the child of a sub-menu then return true
                return domUtils.isDescendantOrInLaw(child, ancestor, getSpouseNode, true);
            }
        }
    });

    MenuFocusMixin._dependencies = [
        {
            mixin: ContainerFocusMixin,
            orderDependent: true
        }
    ];

    return MenuFocusMixin;
});
